/* ============================================================================
   assets/make-offer.js
   ----------------------------------------------------------------------------
   Lógica completa de la experiencia "Make Offer".
   - Vanilla JS, sin dependencias.
   - Diseñado para Shopify + theme Reformation 1.6.1.
   - Integración Klaviyo Onsite (_learnq).
   - Overlay fullscreen (cubre el header del theme).
   ============================================================================ */

(function () {
  'use strict';

  if (window.__makeOfferInitialized) return;
  window.__makeOfferInitialized = true;

  /* ============================================================
     1. UTILS
     ============================================================ */

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /** Formatter de miles (en-US): 2400 → "2,400", 10000.5 → "10,000.50" */
  var numberFormatterInt = new Intl.NumberFormat('en-US');
  var numberFormatterDec = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  /** Formatea un número con separador de miles. Si tiene decimales no enteros, usa 2 decimales. */
  function formatNumber(value) {
    if (value == null || value === '' || isNaN(value)) return '';
    var num = Number(value);
    return Number.isInteger(num)
      ? numberFormatterInt.format(num)
      : numberFormatterDec.format(num);
  }

  /** Formatea un precio Shopify (en centavos) a string con símbolo y separador de miles. */
  function formatPrice(amountInCents, symbol) {
    if (amountInCents == null || isNaN(amountInCents)) return '';
    var normalized = amountInCents / 100;
    return (symbol || '$') + formatNumber(normalized);
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
  }

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  /* ============================================================
     2. ESTADO Y CONFIG
     ============================================================ */

  var dataNode = document.querySelector('[data-make-offer-data]');
  if (!dataNode) return;

  var rawData;
  try {
    rawData = JSON.parse(dataNode.textContent);
  } catch (e) {
    console.error('[MakeOffer] Failed to parse data JSON', e);
    return;
  }

  var CONFIG = rawData.config || {};
  var PRODUCT = rawData.product || {};
  var VARIANTS = rawData.variants || [];
  var INITIAL_VARIANT_ID = rawData.selected_variant_id;
  var CUSTOMER_EMAIL = (rawData.customer && rawData.customer.email) || '';

  var state = {
    mode: 'offer',          // 'offer' | 'buy'
    step: 'form',           // 'form' | 'success'
    currentVariant: null,
    offerAmount: null,
    email: CUSTOMER_EMAIL,
    selectedOption: null,
    isSubmitting: false
  };

  /* ============================================================
     3. RESOLUCIÓN DE BIDS POR VARIANTE
     ============================================================ */

  function getBidsForVariant(variantId) {
    var variantOverride = CONFIG.variants && CONFIG.variants[String(variantId)];
    return {
      good_bid: (variantOverride && variantOverride.good_bid) || CONFIG.good_bid || 0,
      better_bid: (variantOverride && variantOverride.better_bid) || CONFIG.better_bid || 0
    };
  }

  /* ============================================================
     4. MENSAJE DINÁMICO
     ============================================================ */

  function getMessageForAmount(amount, bids, buyNowAmount) {
    var messages = CONFIG.messages || {};
    if (amount == null || amount === '' || isNaN(amount) || amount <= 0) return '';
    var value = Number(amount);
    var good = Number(bids.good_bid);
    var better = Number(bids.better_bid);
    var buyNow = Number(buyNowAmount);

    if (value < good) return messages.below_good_bid || '';
    if (value >= good && value < better) return messages.good_to_better || '';
    if (value === better) return messages.better_bid || '';
    if (value > better && value <= buyNow) return messages.above_better || '';
    return '';
  }

  /* ============================================================
     5. CLASE PRINCIPAL
     ============================================================ */

  function MakeOfferExperience() {
    this.overlay = null;
    this.contentEl = null;
    this.formTemplate = null;
    this.successTemplate = null;
    this.isOpen = false;
    this.previouslyFocused = null;
    this.boundOnKeydown = this.onKeydown.bind(this);
    this.boundOnVariantChange = this.onVariantChange.bind(this);
  }

  /* ---------- Init / Mount ---------- */

  MakeOfferExperience.prototype.init = function () {
    this.setVariantById(INITIAL_VARIANT_ID);

    var overlayTemplate = document.querySelector('[data-make-offer-template]');
    this.formTemplate = document.querySelector('[data-make-offer-form-template]');
    this.successTemplate = document.querySelector('[data-make-offer-success-template]');
    if (!overlayTemplate || !this.formTemplate || !this.successTemplate) {
      console.error('[MakeOffer] Templates missing');
      return;
    }

    var fragment = overlayTemplate.content.cloneNode(true);
    document.body.appendChild(fragment);
    this.overlay = document.querySelector('[data-make-offer-overlay]');
    this.contentEl = $('[data-make-offer-content]', this.overlay);

    // Triggers (puede haber más de uno en la página)
    document.querySelectorAll('[data-make-offer-trigger]').forEach(function (btn) {
      btn.addEventListener('click', this.open.bind(this));
    }, this);

    // Delegación dentro del overlay
    this.overlay.addEventListener('click', this.onOverlayClick.bind(this));

    // Variant change del theme (Reformation 1.6.1)
    document.addEventListener('product:variant-change', this.boundOnVariantChange);

    // Fallback: input[name="id"] del form principal
    var variantInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (variantInput) {
      variantInput.addEventListener('change', function (e) {
        this.setVariantById(e.target.value);
        if (this.isOpen && state.step === 'form') this.renderForm();
      }.bind(this));
    }
  };

  /* ---------- Variant handling ---------- */

  MakeOfferExperience.prototype.setVariantById = function (variantId) {
    var id = Number(variantId);
    var found = VARIANTS.find(function (v) { return v.id === id; });
    if (found) state.currentVariant = found;
    else if (VARIANTS.length) state.currentVariant = VARIANTS[0];
  };

  MakeOfferExperience.prototype.onVariantChange = function (evt) {
    if (!evt.detail || !evt.detail.variant) return;
    this.setVariantById(evt.detail.variant.id);
    if (this.isOpen && state.step === 'form') {
      this.renderForm();
    }
  };

  /* ---------- Open / Close ---------- */

  MakeOfferExperience.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    this.previouslyFocused = document.activeElement;

    document.documentElement.classList.add('make-offer-open');

    // Selección por defecto al abrir: Better Bid (si existe para la variante actual).
    this.applyDefaultSelection();

    this.renderForm();
    this.overlay.hidden = false;

    // Asegurar que el footer esté visible (puede haberse ocultado en un success previo)
    var footer = $('.make-offer-overlay__footer', this.overlay);
    if (footer) footer.style.display = '';

    var backBtn = $('[data-make-offer-close]', this.overlay);
    if (backBtn) backBtn.focus();

    document.addEventListener('keydown', this.boundOnKeydown);
  };

  MakeOfferExperience.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.closeInfoModal();
    this.closeConfirmModal();

    this.overlay.hidden = true;
    document.documentElement.classList.remove('make-offer-open');
    document.removeEventListener('keydown', this.boundOnKeydown);

    // Limpiar estado visual del CTA. Sin esto, si el cliente acaba de hacer
    // Buy o Submit Offer (que añaden .is-loading + disabled al botón), al
    // reabrir el modal el botón aparece bloqueado porque la clase se queda
    // pegada. close() es el punto único de salida, así que centralizamos
    // la limpieza acá.
    var cta = $('[data-make-offer-cta]', this.overlay);
    if (cta) {
      cta.classList.remove('is-loading');
      cta.disabled = false;
    }

    // Reset estado interno
    state.mode = 'offer';
    state.step = 'form';
    state.offerAmount = null;
    state.selectedOption = null;
    state.isSubmitting = false;

    if (this.previouslyFocused && this.previouslyFocused.focus) {
      this.previouslyFocused.focus();
    }
  };

  /* ---------- Keyboard ---------- */

  MakeOfferExperience.prototype.onKeydown = function (e) {
    if (e.key !== 'Escape') return;
    var infoModal = $('[data-make-offer-info-modal]', this.overlay);
    var confirmModal = $('[data-make-offer-confirm-modal]', this.overlay);
    if (infoModal && !infoModal.hidden) { this.closeInfoModal(); return; }
    if (confirmModal && !confirmModal.hidden) { this.closeConfirmModal(); return; }
    this.close();
  };

  /* ---------- Event delegation ---------- */

  MakeOfferExperience.prototype.onOverlayClick = function (e) {
    var target = e.target;
    var closest = function (sel) { return target.closest ? target.closest(sel) : null; };

    if (closest('[data-make-offer-close]'))           { this.close(); return; }
    if (closest('[data-make-offer-info-open]'))       { this.openInfoModal(); return; }
    if (closest('[data-make-offer-info-close]'))      { this.closeInfoModal(); return; }
    if (closest('[data-make-offer-confirm-cancel]'))  { this.closeConfirmModal(); return; }
    if (closest('[data-make-offer-confirm-buy]'))     { this.closeConfirmModal(); this.buyNow(); return; }
    if (closest('[data-make-offer-back-to-offer]')) {
      // Volver a modo oferta limpiando estado previo y aplicando el default
      // (Better Bid), igual que en la primera apertura del modal.
      state.mode = 'offer';
      state.offerAmount = null;
      state.selectedOption = null;
      this.applyDefaultSelection();
      this.renderForm();
      return;
    }
    var optionBtn = closest('[data-make-offer-option]');
    if (optionBtn) { this.selectOption(optionBtn.getAttribute('data-make-offer-option')); return; }
  };

  /* ---------- Render form ---------- */

  MakeOfferExperience.prototype.renderForm = function () {
    state.step = 'form';
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(this.formTemplate.content.cloneNode(true));

    var variant = state.currentVariant;
    if (!variant) return;

    // Imagen
    var img = $('[data-make-offer-image]', this.overlay);
    var imageUrl = variant.featured_image || PRODUCT.featured_image;
    if (img && imageUrl) {
      img.src = imageUrl;
      img.alt = PRODUCT.title || '';
    }

    // Demand badge
    var demandEl = $('[data-make-offer-demand]', this.contentEl);
    if (CONFIG.demand_badge || CONFIG.demand_text) {
      demandEl.hidden = false;
      var badgeEl = $('[data-make-offer-demand-badge]', this.contentEl);
      var textEl = $('[data-make-offer-demand-text]', this.contentEl);
      if (CONFIG.demand_badge) badgeEl.textContent = CONFIG.demand_badge;
      else badgeEl.hidden = true;
      if (CONFIG.demand_text) textEl.textContent = CONFIG.demand_text;
      else textEl.hidden = true;
    }

    // Título y variante
    $('[data-make-offer-title]', this.contentEl).textContent = PRODUCT.title || '';
    var variantEl = $('[data-make-offer-variant]', this.contentEl);
    if (variant.title && variant.title !== 'Default Title') {
      variantEl.textContent = 'Size: ' + variant.title;
    } else {
      variantEl.hidden = true;
    }

    var symbol = CONFIG.currency_symbol || PRODUCT.currency_symbol || '$';
    $('[data-make-offer-currency-symbol]', this.contentEl).textContent = symbol;

    // Pricing options (con formato de miles)
    var bids = getBidsForVariant(variant.id);
    $('[data-make-offer-good-bid]', this.contentEl).textContent = symbol + formatNumber(bids.good_bid);
    $('[data-make-offer-better-bid]', this.contentEl).textContent = symbol + formatNumber(bids.better_bid);
    $('[data-make-offer-buy-now]', this.contentEl).textContent = formatPrice(variant.price, symbol);

    // Buy mode: Item Price card
    $('[data-make-offer-buy-price]', this.contentEl).textContent = formatPrice(variant.price, symbol);

    // Price input
    var priceInput = $('[data-make-offer-price-input]', this.contentEl);
    if (state.offerAmount != null) priceInput.value = formatNumber(state.offerAmount);
    priceInput.addEventListener('input', this.onPriceInput.bind(this));
    priceInput.addEventListener('blur', this.onPriceBlur.bind(this));

    // Email
    var emailInput = $('[data-make-offer-email-input]', this.contentEl);
    emailInput.value = state.email || '';
    emailInput.addEventListener('input', function (e) {
      state.email = e.target.value;
      // Limpiar estado de error
      e.target.classList.remove('is-invalid');
      var err = $('[data-make-offer-email-error]', this.contentEl);
      if (err && !err.hidden) err.hidden = true;
      this.updateCTA();
    }.bind(this));

    if (state.selectedOption) this.markSelectedOption(state.selectedOption);

    this.applyMode();
    this.updateMessage();
    this.updateSubtotal();
    this.updateCTA();
  };

  /* ---------- Render success ---------- */

  MakeOfferExperience.prototype.renderSuccess = function () {
    state.step = 'success';
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(this.successTemplate.content.cloneNode(true));

    var msgEl = $('[data-make-offer-success-message]', this.contentEl);
    msgEl.textContent = CONFIG.success_message ||
      'Your offer was sent successfully. You will receive information within the next 24 hours.';

    var footer = $('.make-offer-overlay__footer', this.overlay);
    if (footer) footer.style.display = 'none';

    var closeBtn = $('[data-make-offer-close]', this.contentEl);
    if (closeBtn) closeBtn.focus();
  };

  /* ---------- Default selection helper ----------
     Aplica Better Bid como selección por defecto (sólo a nivel de estado,
     no toca el DOM). Se llama desde open() antes de renderForm, y desde
     el handler de back-to-offer antes de re-renderizar.

     Solo se aplica si el estado está limpio (offerAmount y selectedOption
     en null), para no sobrescribir valores que ya hubiera puesto el cliente.
  */
  MakeOfferExperience.prototype.applyDefaultSelection = function () {
    if (state.offerAmount != null || state.selectedOption != null) return;
    if (!state.currentVariant) return;
    var bids = getBidsForVariant(state.currentVariant.id);
    if (bids.better_bid && Number(bids.better_bid) > 0) {
      state.offerAmount = Number(bids.better_bid);
      state.selectedOption = 'better_bid';
    }
  };

  /* ---------- Mode (offer / buy) ---------- */

  MakeOfferExperience.prototype.setMode = function (mode) {
    state.mode = mode;
    if (mode === 'offer') {
      state.selectedOption = null;
      this.markSelectedOption(null);
    } else if (mode === 'buy') {
      state.selectedOption = 'buy_now';
      this.markSelectedOption('buy_now');
    }
    this.applyMode();
    this.updateSubtotal();
    this.updateCTA();
  };

  /**
   * applyMode:
   *  - OFFER: muestra pricing-card + email-block; oculta cards de Buy.
   *  - BUY:   oculta pricing-card + email-block; muestra Item Price card + Make An Offer card.
   */
  MakeOfferExperience.prototype.applyMode = function () {
    var pricingCard   = $('[data-make-offer-pricing-card]', this.contentEl);
    var emailBlock    = $('[data-make-offer-email-block]', this.contentEl);
    var buyPriceCard  = $('[data-make-offer-buy-price-card]', this.contentEl);
    var backToOffer   = $('[data-make-offer-back-to-offer]', this.contentEl);
    var ctaLabel      = $('[data-make-offer-cta-label]', this.overlay);

    if (state.mode === 'buy') {
      if (pricingCard)  pricingCard.hidden  = true;
      if (emailBlock)   emailBlock.hidden   = true;
      if (buyPriceCard) buyPriceCard.hidden = false;
      if (backToOffer)  backToOffer.hidden  = false;
      if (ctaLabel)     ctaLabel.textContent = 'Buy';
    } else {
      if (pricingCard)  pricingCard.hidden  = false;
      if (emailBlock)   emailBlock.hidden   = false;
      if (buyPriceCard) buyPriceCard.hidden = true;
      if (backToOffer)  backToOffer.hidden  = true;
      if (ctaLabel)     ctaLabel.textContent = 'Make Offer';
    }
  };

  /* ---------- Option selection ---------- */

  MakeOfferExperience.prototype.selectOption = function (key) {
    var bids = getBidsForVariant(state.currentVariant.id);
    var priceInput = $('[data-make-offer-price-input]', this.contentEl);

    if (key === 'buy_now') { this.setMode('buy'); return; }

    // Good Bid / Better Bid: ya estamos en modo oferta (la pricing card
    // está oculta en modo buy), así que solo aplicamos selección y monto.
    state.selectedOption = key;
    this.markSelectedOption(key);

    if (key === 'good_bid') state.offerAmount = bids.good_bid;
    else if (key === 'better_bid') state.offerAmount = bids.better_bid;

    if (priceInput) priceInput.value = formatNumber(state.offerAmount);
    this.updateMessage();
    this.updateSubtotal();
    this.updateCTA();
  };

  MakeOfferExperience.prototype.markSelectedOption = function (key) {
    var options = this.contentEl.querySelectorAll('[data-make-offer-option]');
    options.forEach(function (opt) {
      if (opt.getAttribute('data-make-offer-option') === key) {
        opt.classList.add('is-selected');
        opt.setAttribute('aria-pressed', 'true');
      } else {
        opt.classList.remove('is-selected');
        opt.setAttribute('aria-pressed', 'false');
      }
    });
  };

  /* ---------- Input handlers ----------
     Formato en tiempo real con separador de miles. Preserva la posición
     del cursor contando dígitos antes/después del cambio para evitar saltos.
  */
  MakeOfferExperience.prototype.onPriceInput = function (e) {
    var input = e.target;
    var oldValue = input.value;
    var cursorPos = (typeof input.selectionStart === 'number') ? input.selectionStart : oldValue.length;

    // Contar dígitos antes del cursor en el valor viejo
    var digitsBeforeCursor = 0;
    for (var i = 0; i < cursorPos; i++) {
      var ch = oldValue.charAt(i);
      if (ch >= '0' && ch <= '9') digitsBeforeCursor++;
    }

    // Limpiar todo lo que no sea dígito y reformatear
    var digits = oldValue.replace(/[^\d]/g, '');
    var newValue = digits === '' ? '' : formatNumber(Number(digits));

    if (newValue !== oldValue) {
      input.value = newValue;
      // Restaurar cursor manteniendo el conteo de dígitos
      var newCursorPos = 0;
      var counted = 0;
      while (newCursorPos < newValue.length && counted < digitsBeforeCursor) {
        var c = newValue.charAt(newCursorPos);
        if (c >= '0' && c <= '9') counted++;
        newCursorPos++;
      }
      try { input.setSelectionRange(newCursorPos, newCursorPos); } catch (err) { /* noop */ }
    }

    state.offerAmount = digits === '' ? null : Number(digits);

    // Auto-match: si el monto coincide con good_bid o better_bid de la variante
    // actual, marcamos esa opción como seleccionada automáticamente.
    var matched = null;
    var variant = state.currentVariant;
    if (variant && state.offerAmount != null) {
      var bids = getBidsForVariant(variant.id);
      if (state.offerAmount === Number(bids.good_bid)) matched = 'good_bid';
      else if (state.offerAmount === Number(bids.better_bid)) matched = 'better_bid';
    }
    state.selectedOption = matched;
    this.markSelectedOption(matched);

    this.updateMessage();
    this.updateSubtotal();
    this.updateCTA();
  };

  MakeOfferExperience.prototype.onPriceBlur = function () {
    var variant = state.currentVariant;
    if (!variant) return;
    var buyNowUnits = variant.price / 100;
    if (state.offerAmount != null && state.offerAmount > buyNowUnits) {
      this.openConfirmModal();
    }
  };

  /* ---------- Mensaje dinámico ---------- */

  MakeOfferExperience.prototype.updateMessage = function () {
    var msgEl = $('[data-make-offer-message]', this.contentEl);
    if (!msgEl) return;
    var variant = state.currentVariant;
    if (!variant) {
      msgEl.textContent = '';
      msgEl.hidden = true;
      return;
    }
    var bids = getBidsForVariant(variant.id);
    var buyNowUnits = variant.price / 100;
    var message = getMessageForAmount(state.offerAmount, bids, buyNowUnits);
    msgEl.textContent = message;
    // Ocultar el bloque completo cuando no hay mensaje para evitar el espacio
    // en blanco residual del min-height. Cuando aparece un mensaje, hidden=false
    // permite que aria-live anuncie el texto a lectores de pantalla.
    msgEl.hidden = !message;
  };

  /* ---------- Subtotal ---------- */

  MakeOfferExperience.prototype.updateSubtotal = function () {
    var subEl = $('[data-make-offer-subtotal]', this.overlay);
    if (!subEl) return;
    var symbol = CONFIG.currency_symbol || PRODUCT.currency_symbol || '$';
    if (state.mode === 'buy') {
      subEl.textContent = formatPrice(state.currentVariant.price, symbol);
    } else if (state.offerAmount != null && state.offerAmount > 0) {
      subEl.textContent = symbol + formatNumber(state.offerAmount);
    } else {
      subEl.textContent = '—';
    }
  };

  /* ---------- CTA ----------
     OFFER MODE: el CTA se habilita cuando hay precio > 0 (sin requerir email).
                 El email se valida al hacer clic; si falla, focus + alert inline.
     BUY MODE:   habilitado si la variante está disponible.
  */
  MakeOfferExperience.prototype.updateCTA = function () {
    var cta = $('[data-make-offer-cta]', this.overlay);
    if (!cta) return;
    if (state.isSubmitting) { cta.disabled = true; return; }

    if (state.mode === 'buy') {
      cta.disabled = !state.currentVariant || !state.currentVariant.available;
    } else {
      var hasPrice = state.offerAmount != null && state.offerAmount > 0;
      cta.disabled = !hasPrice;
    }
    cta.onclick = this.onCTAClick.bind(this);
  };

  MakeOfferExperience.prototype.onCTAClick = function () {
    if (state.isSubmitting) return;
    if (state.mode === 'buy') this.buyNow();
    else this.submitOffer();
  };

  /* ---------- Buy Now ---------- */

  MakeOfferExperience.prototype.buyNow = function () {
    if (!state.currentVariant) return;
    state.isSubmitting = true;
    this.updateCTA();
    var cta = $('[data-make-offer-cta]', this.overlay);
    if (cta) cta.classList.add('is-loading');

    var formData = new FormData();
    formData.append('id', state.currentVariant.id);
    formData.append('quantity', 1);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        // Notificar al theme para que actualice/abra el cart drawer.
        // Reformation 1.6.1 escucha estos eventos y abre el drawer solo.
        // Si en algún momento necesitas forzar redirect al carrito, descomenta:
        //   window.location.href = '/cart';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('product:added-to-cart', { bubbles: true }));
        this.close();
      }.bind(this))
      .catch(function (err) {
        console.error('[MakeOffer] Buy Now failed', err);
        alert('Sorry, something went wrong. Please try again.');
        state.isSubmitting = false;
        if (cta) cta.classList.remove('is-loading');
        this.updateCTA();
      }.bind(this));
  };

  /* ---------- Submit Offer → Klaviyo ---------- */

  MakeOfferExperience.prototype.submitOffer = function () {
    // Validación email inline (sin disable del CTA)
    if (!isValidEmail(state.email)) {
      var emailInput = $('[data-make-offer-email-input]', this.contentEl);
      var errEl = $('[data-make-offer-email-error]', this.contentEl);
      if (errEl) {
        errEl.textContent = state.email
          ? 'Please enter a valid email address.'
          : 'Please enter your email to receive a response.';
        errEl.hidden = false;
      }
      if (emailInput) {
        emailInput.classList.add('is-invalid');
        emailInput.focus();
        if (typeof emailInput.scrollIntoView === 'function') {
          emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }
    if (state.offerAmount == null || state.offerAmount <= 0) return;

    // Si la oferta supera el precio Buy Now, mostrar modal de confirmación
    // antes de enviar a Klaviyo. El cliente puede confirmar la compra directa
    // (botón Buy Now en el modal) o cancelar y ajustar la oferta.
    // Importante: el modal se vuelve a mostrar cada vez que el cliente intente
    // enviar con un valor por encima del Buy Now, incluso si ya lo cerró antes.
    var variant = state.currentVariant;
    if (variant && state.offerAmount > variant.price / 100) {
      this.openConfirmModal();
      return;
    }

    state.isSubmitting = true;
    this.updateCTA();
    var cta = $('[data-make-offer-cta]', this.overlay);
    if (cta) cta.classList.add('is-loading');

    var variant = state.currentVariant;
    var symbol = CONFIG.currency_symbol || PRODUCT.currency_symbol || '$';

    var payload = {
      email: state.email,
      product_name: PRODUCT.title,
      product_id: PRODUCT.id,
      variant_id: variant.id,
      variant_title: variant.title,
      offered_price: state.offerAmount,
      current_price: variant.price / 100,
      currency: PRODUCT.currency,
      currency_symbol: symbol,
      product_url: window.location.origin + PRODUCT.url,
      product_image: variant.featured_image || PRODUCT.featured_image,
      timestamp: new Date().toISOString()
    };

    try {
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', { '$email': state.email }]);
      window._learnq.push(['track', 'Make Offer Submitted', payload]);
    } catch (e) {
      console.warn('[MakeOffer] Klaviyo push failed', e);
    }

    setTimeout(function () {
      state.isSubmitting = false;
      if (cta) cta.classList.remove('is-loading');
      this.renderSuccess();
    }.bind(this), 400);
  };

  /* ---------- Info Modal ---------- */

  MakeOfferExperience.prototype.openInfoModal = function () {
    var modal = $('[data-make-offer-info-modal]', this.overlay);
    if (!modal) return;
    var info = CONFIG.info_modal || {};
    $('[data-make-offer-info-title]', modal).textContent = info.title || 'Pricing Options';
    var contentEl = $('[data-make-offer-info-content]', modal);
    contentEl.innerHTML = '';
    String(info.content || '').split(/\n\n+/).forEach(function (para) {
      var p = document.createElement('p');
      p.textContent = para;
      contentEl.appendChild(p);
    });
    modal.hidden = false;
    var closeBtn = modal.querySelector('[data-make-offer-info-close]');
    if (closeBtn) closeBtn.focus();
  };

  MakeOfferExperience.prototype.closeInfoModal = function () {
    var modal = $('[data-make-offer-info-modal]', this.overlay);
    if (modal) modal.hidden = true;
  };

  /* ---------- Confirm Modal (oferta > Buy Now) ---------- */

  MakeOfferExperience.prototype.openConfirmModal = function () {
    var modal = $('[data-make-offer-confirm-modal]', this.overlay);
    if (!modal) return;
    var symbol = CONFIG.currency_symbol || PRODUCT.currency_symbol || '$';
    $('[data-make-offer-confirm-offer]', modal).textContent = symbol + formatNumber(state.offerAmount);
    var askValue = formatPrice(state.currentVariant.price, symbol);
    $('[data-make-offer-confirm-ask]', modal).textContent = askValue;
    $('[data-make-offer-confirm-ask-inline]', modal).textContent = askValue;
    modal.hidden = false;
    var buyBtn = modal.querySelector('[data-make-offer-confirm-buy]');
    if (buyBtn) buyBtn.focus();
  };

  MakeOfferExperience.prototype.closeConfirmModal = function () {
    var modal = $('[data-make-offer-confirm-modal]', this.overlay);
    if (modal) modal.hidden = true;
  };

  /* ============================================================
     6. BOOTSTRAP
     ============================================================ */
  onReady(function () {
    var experience = new MakeOfferExperience();
    experience.init();
    window.MakeOffer = experience;
  });
})();